/**
 * 常驻数据 store
 */
import { projectApi } from '@/api/common/project';
import { roleApi } from '@/api/common/role';
import { userApi } from '@/api/common/user';
import { trControlApi } from '@/api/production/trControl';
import { adminProductSeriesApi } from '@/api/system/adminProductSeries';
import { userOrganizationApi } from '@/api/system/userOrganization';
import type { PostItem, ResponsibleItem } from '@/sites/project/pages/stage-review/type';
import { RoleEnable, type ProductSeries, type Role, type SysProject } from '@/types/common';
import type { Factory } from '@/types/common/factory';
import { type User } from '@/types/common/user';
import { throttle } from 'lodash';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export const useResidentStore = defineStore('resident', () => {
  const allUser = ref<User[]>([]);
  const allSysProject = ref<SysProject[]>([]);
  const userSysProjects = ref<SysProject[]>([]);
  const allProductSeries = ref<ProductSeries[]>([]);
  const allRole = ref<Role[]>([]);
  const allOrganizations = ref<any[]>([]);
  const postList = ref<PostItem[]>([]);
  const responsibleList = ref<ResponsibleItem[]>([]);

  const requestUser = throttle(userApi.getAllUser, 3000);
  const requestSysProject = throttle(projectApi.getAllProjects, 3000);
  const requestUserSysProject = throttle(projectApi.getUserSysProject, 3000);
  const requestProductSeries = throttle(adminProductSeriesApi.getAll, 3000);
  const requestRole = throttle(roleApi.getAll, 3000);
  const requestOrganizations = throttle(() => userOrganizationApi.getList({ pageSize: 999, pageIndex: 1 }), 3000);

  // 默认工厂
  const currentFactory = ref<Factory>(
    JSON.parse(
      localStorage.getItem('ctcFactory') || '{"id": 100, "name": "国内工厂"}',
      // localStorage.getItem('ctcFactory') || '{"id": 100, "name": "惠州泓锋"}',
    ),
  );

  const nonUserSysProject = computed(() => {
    const ownedIds = userSysProjects.value.map((project) => project.id);
    return allSysProject.value.filter((project) => !ownedIds.includes(project.id));
  });

  const allNormalRole = computed(() => allRole.value.filter((role) => role.enable === RoleEnable.Enable));

  async function getAllUser() {
    if (allUser.value.length === 0) {
      const resp = await requestUser();
      if (resp.code === 0) {
        allUser.value = resp.data;
      }
    }
    return allUser.value;
  }

  async function getAllSysProject(force?: boolean) {
    if (allSysProject.value.length === 0 || force) {
      const resp = await requestSysProject();
      if (resp.code === 0) {
        allSysProject.value = resp.data;
      }
    }
    return allSysProject.value;
  }

  async function getUserSysProject(force?: boolean) {
    if (userSysProjects.value.length === 0 || force) {
      const projects = await getAllSysProject();
      const resp = await requestUserSysProject();
      if (resp.code === 0) {
        const ids = resp.data.map((e: any) => e.id);
        userSysProjects.value = projects.filter((p) => ids.includes(p.id));
      }
    }
    return userSysProjects.value;
  }

  async function getAllProductSeries() {
    if (allProductSeries.value.length === 0) {
      const resp = await requestProductSeries();
      if (resp.code === 0) {
        allProductSeries.value = resp.data.list;
      }
    }
    return allProductSeries.value;
  }

  async function getAllRole() {
    if (allRole.value.length === 0) {
      const resp = await requestRole();
      if (resp.code === 0) {
        allRole.value = resp.data.filter((role) => role.type !== 'system');
      }
    }
    return allRole.value;
  }

  async function getUserById(id: number) {
    const users = await getAllUser();
    return users.find((user) => user.id === id);
  }

  async function getSysProjectById(id: number) {
    const projects = await getAllSysProject();
    return projects.find((project) => project.id === id);
  }

  async function getSysProjectByCode(code: string) {
    const projects = await getAllSysProject();
    return projects.find((project) => project.code === code);
  }

  async function getProductSeriesById(id: number) {
    const series = await getAllProductSeries();
    return series.find((s) => s.id === id);
  }

  async function getRoleById(id: string) {
    const roles = await getAllRole();
    return roles.find((role) => role.id === id);
  }

  async function getAllOrganizations() {
    if (allOrganizations.value.length === 0) {
      const resp = await requestOrganizations();
      if (resp.code === 0) {
        allOrganizations.value = resp.data.list;
      }
    }
    return allOrganizations.value;
  }

  async function getOrganizationById(id: number) {
    const organizations = await getAllOrganizations();
    return organizations.find((org) => org.orgId === id);
  }

  async function getPostByProjectId(projectId: number) {
    const res = await trControlApi.getPosition(projectId);
    if (res.code === 0) {
      postList.value = res.data;
    } else {
      postList.value = [];
    }
  }

  async function getResponsibleByProjectId(projectId: number) {
    const res = await trControlApi.getResponsible(projectId);
    if (res.code === 0) {
      responsibleList.value = res.data;
    } else {
      responsibleList.value = [];
    }
  }

  /**
   * 重置会话级缓存（账号切换场景）。
   *
   * 为什么需要：setup 风格的 Pinia store 默认没有 $reset()，且这些 ref
   * 的 getX() 遵循"length === 0 ? 拉新 : 用缓存"，登出/切账号若不显式清，
   * 下一个账号会看到上一个账号的数据（如"我的项目/产品"弹窗）。
   *
   * 保留 currentFactory 不动：工厂是终端偏好（localStorage 持久化），不随账号切换重置。
   */
  function reset() {
    allUser.value = [];
    allSysProject.value = [];
    userSysProjects.value = [];
    allProductSeries.value = [];
    allRole.value = [];
    allOrganizations.value = [];
    postList.value = [];
    responsibleList.value = [];
  }

  return {
    allUser,
    allSysProject,
    userSysProjects,
    nonUserSysProject,
    allProductSeries,
    allRole,
    allNormalRole,
    allOrganizations,
    postList,
    responsibleList,
    currentFactory,
    getAllUser,
    getUserById,
    getAllSysProject,
    getUserSysProject,
    getSysProjectById,
    getAllProductSeries,
    getSysProjectByCode,
    getProductSeriesById,
    getRoleById,
    getAllRole,
    getAllOrganizations,
    getOrganizationById,
    getPostByProjectId,
    getResponsibleByProjectId,
    reset,
  };
});
